import { zodResolver } from "@hookform/resolvers/zod"
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import { type FC, useCallback, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { type AddContactSubscriptionFormValues, AddContactSubscriptionSchema } from "./AddContactSubscriptionSchema"

import { Button } from "@/components/common/Button"
import { ChipBackground, SelectDialogContent } from "@/components/common/Modals"
import type { ContactSubscription } from "@/graphql/codegen/graphql"
import { useGetSubscription } from "@/screens/Contacts/edit/hooks/useGetSubscription"
import type { DialogCloseAction } from "@/types/common"

interface Props {
  open: boolean
  contactId: string
  name: string
  companyName?: string
  onClose: (action: DialogCloseAction) => void
  onSubmit: () => void
}

const defaultSelectedSubscriptionList: ContactSubscription[] = []

export const ContactSubscriptionDialog: FC<Props> = ({ open, contactId, name, companyName, onClose, onSubmit }) => {
  const {
    selectedSubscriptionList = defaultSelectedSubscriptionList,
    selectedSubscriptionDetailsFetching,
    subscriptionList = [],
    subscriptionListDetailsFetching,
    reexecuteQueryWithReset,
    updateSubscriptions,
  } = useGetSubscription(contactId)

  const methods = useForm<AddContactSubscriptionFormValues>({
    defaultValues: {
      batchContactSubscriptionsToCreate: [],
    },
    resolver: zodResolver(AddContactSubscriptionSchema),
    mode: "all",
  })

  useEffect(() => {
    methods.reset({
      batchContactSubscriptionsToCreate:
        selectedSubscriptionList?.map(({ contactSubscriptionId, name }) => ({
          contactSubscriptionId,
          name,
        })) ?? [],
    })
  }, [selectedSubscriptionList, methods])

  const onHandleSubmit = useCallback(
    async (data: AddContactSubscriptionFormValues) => {
      const subscriptions = data.batchContactSubscriptionsToCreate.map(
        ({ contactSubscriptionId }) => contactSubscriptionId
      )

      await updateSubscriptions({
        input: {
          contactId,
          subscriptions,
        },
      })
      onSubmit()
      methods.reset()
      onClose("success")
      reexecuteQueryWithReset()
    },
    [onSubmit, methods, onClose, reexecuteQueryWithReset, contactId, updateSubscriptions]
  )

  const isButtonEnabled = methods.formState.isValid && methods.formState.isDirty

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        methods.reset()
        onClose(reason)
      }}
      fullWidth
    >
      <FormProvider {...methods}>
        <DialogTitle className='pb-3 pl-10 font-normal text-primary'>
          {name}
          {companyName && (
            <Typography variant='subtitle1' className='text-gray-700'>
              {companyName}
            </Typography>
          )}
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={() => {
            methods.reset()
            onClose("cancel")
          }}
          className='absolute right-4 top-4 text-primary'
        >
          <CloseOutlined />
        </IconButton>
        <Divider />
        <DialogContent classes={{ root: "p-10" }}>
          {subscriptionListDetailsFetching || selectedSubscriptionDetailsFetching ? (
            <CircularProgress />
          ) : (
            <SelectDialogContent<ContactSubscription>
              options={subscriptionList}
              title='Subscriptions'
              label='Add all known subscriptions for this contact'
              idField='contactSubscriptionId'
              nameField='name'
              chipBackground={ChipBackground.YELLOW}
              controlName='batchContactSubscriptionsToCreate'
            />
          )}
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button
            appearance='outlined'
            onClick={() => {
              methods.reset()
              onClose("cancel")
            }}
          >
            Cancel
          </Button>
          <Button onClick={methods.handleSubmit(onHandleSubmit)} disabled={!isButtonEnabled}>
            Save
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
